import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { FileCheck, Pencil, Trash2, Loader2, CheckCircle, Search, ChevronLeft, ChevronRight, X, ArrowUp, ArrowDown } from "lucide-react";
import {
  useTabSubmissions,
  useUpdateTabSubmission,
  useDeleteTabSubmission,
  useApproveTabSubmission,
  type TabSubmission,
} from "@api/tabSubmissions";
import { useArtists } from "@api/artists";
import { t } from "@lib/i18n";

const PAGE_SIZE = 20;

export function TabSubmissions() {
  const [editingSubmission, setEditingSubmission] = useState<TabSubmission | null>(null);
  const [approvingSubmission, setApprovingSubmission] = useState<TabSubmission | null>(null);
  const [viewingSubmission, setViewingSubmission] = useState<TabSubmission | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page when search changes
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  const { data: submissionsData, isLoading, error } = useTabSubmissions({
    page,
    page_size: PAGE_SIZE,
    search: debouncedSearch || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ArrowUp className="w-3 h-3 ml-1 inline" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 inline" />
    );
  };
  
  const { data: artists } = useArtists();
  const updateMutation = useUpdateTabSubmission();
  const deleteMutation = useDeleteTabSubmission();
  const approveMutation = useApproveTabSubmission();

  const handleDelete = (id: number) => {
    if (confirm(t("tabSubmissions.deleteConfirm"))) {
      deleteMutation.mutate(id);
    }
  };

  const handleApprove = (submission: TabSubmission) => {
    setApprovingSubmission(submission);
  };

  const confirmApprove = (artistId: number) => {
    if (approvingSubmission) {
      approveMutation.mutate(
        { id: approvingSubmission.id, artistId },
        {
          onSuccess: () => {
            setApprovingSubmission(null);
          },
        }
      );
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <FileCheck className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">{t("tabSubmissions.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("tabSubmissions.description")}
          </p>
        </div>
      </div>

      {/* Submission Detail View */}
      {viewingSubmission && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setViewingSubmission(null)}
          />
          <Card className="fixed inset-2 z-50 overflow-auto bg-background shadow-2xl w-[calc(100%-1rem)]">
          <CardHeader className="sticky top-0 bg-background border-b z-10">
            <div className="flex items-center justify-between">
              <CardTitle>{viewingSubmission.title}</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewingSubmission(null)}
                className="gap-1"
              >
                <X className="w-4 h-4" />
                {t("common.close")}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">{t("common.id")}:</span>
                <span className="ml-2 font-medium">{viewingSubmission.id}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t("tabSubmissions.artistLabel")}:</span>
                <span className="ml-2 font-medium">{viewingSubmission.artist}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t("tabs.userLabel")}:</span>
                <span className="ml-2 font-medium">{viewingSubmission.user_email || `User #${viewingSubmission.user_id}`}</span>
              </div>
              {viewingSubmission.created_at && (
                <div>
                  <span className="text-muted-foreground">{t("common.createdAt")}:</span>
                  <span className="ml-2 font-medium">
                    {new Date(viewingSubmission.created_at).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
            
            <div>
              <h3 className="text-sm font-medium mb-2">{t("tabs.bodyLabel")}</h3>
              <div className="bg-muted p-4 rounded-md">
                <pre className="whitespace-pre-wrap font-mono text-sm">{viewingSubmission.body}</pre>
              </div>
            </div>
            
            {viewingSubmission.body_rendered && (
              <div>
                <h3 className="text-sm font-medium mb-2">{t("tabs.bodyRenderedLabel")}</h3>
                <div className="bg-muted p-4 rounded-md">
                  <div 
                    className="prose prose-sm max-w-none dark:prose-invert"
                    dangerouslySetInnerHTML={{ __html: viewingSubmission.body_rendered }}
                  />
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setViewingSubmission(null);
                  handleApprove(viewingSubmission);
                }}
                className="gap-1 text-primary"
              >
                <CheckCircle className="w-4 h-4" />
                {t("tabSubmissions.approve")}
              </Button>
              <Button
                variant="outline"
                onClick={() => setViewingSubmission(null)}
              >
                {t("common.close")}
              </Button>
            </div>
          </CardContent>
        </Card>
        </>
      )}

      {/* Approve Dialog */}
      {approvingSubmission && (
        <Card>
          <CardHeader>
            <CardTitle>{t("tabSubmissions.approveTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("tabSubmissions.approveDescription")}
            </p>
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t("tabSubmissions.selectArtist")}
              </label>
              <Select
                onValueChange={(value) => confirmApprove(parseInt(value))}
                disabled={approveMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("tabSubmissions.artistPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {artists?.list?.map((artist) => (
                    <SelectItem key={artist.id} value={artist.id.toString()}>
                      {artist.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setApprovingSubmission(null)}
                disabled={approveMutation.isPending}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submissions List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t("tabSubmissions.pendingSubmissions")}</CardTitle>
            {submissionsData && (
              <div className="text-sm text-muted-foreground">
                {t("common.showing")} {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, submissionsData.total)} {t("common.of")} {submissionsData.total}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t("common.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="text-sm text-destructive py-4">
              {t("tabSubmissions.errorLoading")}: {error instanceof Error ? error.message : t("common.unknownError")}
            </div>
          )}

          {!isLoading && !error && submissionsData && submissionsData.list.length === 0 && (
            <p className="text-muted-foreground py-4">{t("tabSubmissions.noSubmissions")}</p>
          )}

          {!isLoading && !error && submissionsData && submissionsData.list.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("id")}
                      >
                        {t("common.id")}
                        <SortIcon column="id" />
                      </th>
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("title")}
                      >
                        {t("tabSubmissions.titleLabel")}
                        <SortIcon column="title" />
                      </th>
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("artist")}
                      >
                        {t("tabSubmissions.artistLabel")}
                        <SortIcon column="artist" />
                      </th>
                      <th className="text-left p-2 font-medium">{t("users.emailLabel")}</th>
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("created_at")}
                      >
                        {t("common.createdAt")}
                        <SortIcon column="created_at" />
                      </th>
                      <th className="text-right p-2 font-medium">{t("common.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissionsData.list.map((submission) => (
                      <tr 
                        key={submission.id} 
                        className="border-b hover:bg-muted/50 cursor-pointer"
                        onClick={() => setViewingSubmission(submission)}
                      >
                        <td className="p-2">{submission.id}</td>
                        <td className="p-2 font-medium">{submission.title}</td>
                        <td className="p-2 text-muted-foreground">{submission.artist}</td>
                        <td className="p-2 text-muted-foreground">{submission.user_email || `User #${submission.user_id}`}</td>
                        <td className="p-2 text-muted-foreground text-sm">
                          {submission.created_at ? new Date(submission.created_at).toLocaleDateString() : "-"}
                        </td>
                        <td className="p-2" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApprove(submission)}
                              disabled={approveMutation.isPending}
                              className="gap-1 text-primary"
                            >
                              <CheckCircle className="w-4 h-4" />
                              {t("tabSubmissions.approve")}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(submission.id)}
                              disabled={deleteMutation.isPending}
                              className="gap-1 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t("common.delete")}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {submissionsData.total_pages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    {t("common.page")} {submissionsData.page} {t("common.of")} {submissionsData.total_pages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1 || isLoading}
                      className="gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {t("common.previous")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(submissionsData.total_pages, p + 1))}
                      disabled={page === submissionsData.total_pages || isLoading}
                      className="gap-1"
                    >
                      {t("common.next")}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

